import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { MetricEnvelope, MetricUnit } from '@elutide/shared';
import { AgeStamp, BasisBadge } from './Provenance';
import { DefinitionPopover } from './DefinitionPopover';
import { ageClassForBasis, formatByUnit, formatMetricValue } from '@/lib/format';

export interface KpiTileProps {
  label: string;
  /** The metric layer's envelope. Screens fed by a page contract pass `value` instead. */
  envelope?: MetricEnvelope | null | undefined;
  /**
   * A number straight from a page contract, for screens that are served by one request
   * rather than by the metric layer. Null renders "no data", never 0.
   */
  value?: number | null;
  unit?: MetricUnit;
  /** Comparison text, already computed by the caller from a second envelope. */
  delta?: { text: string; tone?: 'ok' | 'warn' | 'crit' } | null;
  /** Secondary line, for example "oldest 19h". */
  note?: string | null;
  href?: string;
  tone?: 'ok' | 'warn' | 'crit';
  showCents?: boolean;
  /**
   * The basis badge and age stamp. On by default, because a metric without provenance is a
   * rumour. A screen whose whole page carries one age stamp turns it off per tile.
   */
  showProvenance?: boolean;
  /** Turns the tile into a filter control. Mutually exclusive with `href`. */
  onClick?: () => void;
  pressed?: boolean;
  /** The one sentence definition, from /legend. Shown on hover and on keyboard focus. */
  definition?: string | null;
  /** Extra content under the number, for example a Sparkline. */
  children?: ReactNode;
}

/**
 * The number at display size, its provenance and its age. A tile whose value is null renders
 * "no data". It never renders 0 as a stand-in for unknown.
 */
export function KpiTile({
  label,
  envelope,
  value,
  unit = 'count',
  delta,
  note,
  href,
  tone,
  showCents,
  showProvenance = true,
  onClick,
  pressed,
  definition,
  children,
}: KpiTileProps) {
  const fromEnvelope = envelope !== undefined;
  const hasValue = fromEnvelope
    ? Boolean(envelope) && envelope?.value !== null
    : value !== null && value !== undefined;

  const moneyOptions = showCents === undefined ? {} : { showCents };
  const text = fromEnvelope
    ? formatMetricValue(envelope, moneyOptions)
    : hasValue
      ? formatByUnit(value as number, unit, moneyOptions)
      : 'no data';

  const valueClass = [
    'o-kpi__value',
    !hasValue ? 'o-kpi__value--none' : tone ? `o-kpi__value--${tone}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      <span className="o-kpi__label">{label}</span>
      <span className={valueClass}>{text}</span>
      {showProvenance && envelope ? (
        <span className="o-kpi__meta">
          <BasisBadge
            basis={envelope.basis}
            window={envelope.window}
            partial={envelope.partial}
          />
          <AgeStamp asOf={envelope.as_of} klass={ageClassForBasis(envelope.basis)} />
        </span>
      ) : null}
      {delta ? (
        <span
          className="o-kpi__meta"
          style={delta.tone ? { color: `var(--${delta.tone})` } : undefined}
        >
          {delta.text}
        </span>
      ) : null}
      {note ? <span className="o-kpi__meta">{note}</span> : null}
      {children ? <span className="o-kpi__extra">{children}</span> : null}
    </>
  );

  if (href) {
    return (
      <DefinitionPopover definition={definition}>
        <Link to={href} className="o-kpi">
          {body}
        </Link>
      </DefinitionPopover>
    );
  }

  if (onClick) {
    return (
      <DefinitionPopover definition={definition}>
        <button
          type="button"
          className="o-kpi o-kpi--action"
          aria-pressed={pressed ?? false}
          onClick={onClick}
        >
          {body}
        </button>
      </DefinitionPopover>
    );
  }

  return (
    <DefinitionPopover definition={definition}>
      <div className="o-kpi">{body}</div>
    </DefinitionPopover>
  );
}
